import type { Brief } from '../lib/brief'
// Thin fetch wrapper. All admin endpoints live under /api/*.
// Auth is cookie-based; the browser handles it for us (credentials: include).

export class ApiError extends Error {
  status: number
  detail: unknown
  constructor(status: number, detail: unknown, message?: string) {
    super(message ?? `API error ${status}`)
    this.status = status
    this.detail = detail
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: 'include',
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })
  const ct = res.headers.get('content-type') ?? ''
  const payload: unknown = ct.includes('application/json') ? await res.json() : await res.text()
  if (!res.ok) {
    throw new ApiError(res.status, payload)
  }
  return payload as T
}

/** Multipart POST. Deliberately sets no Content-Type: the browser must
 *  supply it so the multipart boundary matches the body it encodes. */
async function requestForm<T>(path: string, form: FormData): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'include',
    body: form,
  })
  const ct = res.headers.get('content-type') ?? ''
  const payload: unknown = ct.includes('application/json')
    ? await res.json()
    : await res.text()
  if (!res.ok) {
    throw new ApiError(res.status, payload)
  }
  return payload as T
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  postForm: <T>(path: string, form: FormData) => requestForm<T>(path, form),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  del: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body),
}

// ---------- Types shared with backend ----------

export type Publication = {
  id: number
  slug: string
  name: string
  mailgun_active_list_address: string | null
}

export type Subscriber = {
  id: number
  simplecirc_account_id: string
  /** 8 digits — the account number we print, quote and take over the
   *  phone. Backfilled across the SimpleCirc population, so it is set for
   *  all but a handful of pre-mint rows. Same shape as
   *  simplecirc_account_id above — they are told apart by which field they
   *  arrive in, not by looking at them. */
  member_account_number: string | null
  email: string | null
  title: string | null
  first_name: string | null
  last_name: string | null
  city: string | null
  state: string | null
  zipcode: string | null
  country: string | null
  email_suppressed_at: string | null
  upstream_deleted_at: string | null
  upstream_deleted_reason?: string | null
  last_synced_at: string | null
  created_at: string
  notes: string | null
  renewal_link?: string | null
  login_link?: string | null
}

export type Subscription = {
  id: number
  publication_slug: string
  publication_name: string
  simplecirc_subscription_id: string
  status: string
  issues_remaining: number
  /** Whole months to current_period_end; null when it never expires. */
  months_left: number | null
  current_period_end: string | null
  started_at: string | null
  expired_at: string | null
  do_not_renew: boolean
  never_expires: boolean
  // Channel + payment snapshot
  subscription_type: string | null
  auto_renew: boolean
  auto_renew_status: string | null
  auto_renew_price: string | null
  payment_source: string | null
  payment_type: string | null
  payment_new_or_renewal: string | null
  payment_custom_source: string | null
  agency_name: string | null
  last_payment_amount: string | null
  last_payment_date: string | null
  last_payment_order_id: string | null
  raw_csv_row: Record<string, string> | null
  raw_csv_imported_at: string | null
  digital_status?: string | null
  last_publish_date?: string | null
  last_volume?: number | null
  last_issue?: number | null
  promo_code?: string | null
}

export type SubscriberDetail = Subscriber & {
  address_1: string | null
  address_2: string | null
  /** Staff may edit name and address here. False while SimpleCirc's
   *  import still rewrites them — edit those in SimpleCirc. */
  identity_editable: boolean
  /** False when every one of the member's titles is live (cut over): no Resync then. */
  simplecirc_resync: boolean
  subscriptions: Subscription[]
}

export type EventSubject = {
  kind: 'subscriber' | 'email'
  account_id: string | null
  label: string
  email: string | null
  /** null when we've never ingested the account, or for Mailgun rows */
  subscriber_id: number | null
}

export type WebhookEvent = {
  id: number
  source: string
  event_type: string
  publication_slug: string | null
  processed_status: string
  received_at: string
  processed_at: string | null
  error: string | null
  /** /api/events only — the dashboard's compact event table omits it */
  subject?: EventSubject | null
}

export type EmailLogEntry = {
  id: number
  occurred_at: string
  send_type: string
  source: string
  subscriber_id: number | null
  to_email: string
  from_email: string | null
  from_domain: string
  publication_slug: string | null
  subject: string | null
  template_slug: string | null
  campaign_id: number | null
  tags: string[]
  mailgun_message_id: string | null
  status: string
  status_detail: string | null
  last_event_at: string | null
  /** List-only hints so the row can enable/disable Resend without
   *  fetching every body. */
  can_resend: boolean
  resend_blocked_reason: string | null
}

/** Detail route only — the list endpoint deliberately omits bodies. */
export type EmailLogDetail = EmailLogEntry & {
  events: Array<Record<string, unknown>>
  body_html: string | null
  body_text: string | null
  /** Set when the 30-day pruner cleared the body. Null here with a null
   *  body means it was never captured, which is a different story. */
  body_pruned_at: string | null
}

export type EmailLogResend = {
  /** False for a recorded refusal — the send was processed and declined. */
  ok: boolean
  status: string
  detail: string | null
  email_log_id: number
}

export type EmailLogPage = {
  items: EmailLogEntry[]
  next_cursor: string | null
  coverage_gaps: Array<{ begin: string; end: string; reason: string }>
}

export type Segment = {
  id: number
  slug: string
  name: string
  description: string | null
  mailgun_list_address: string | null
  enabled: boolean
  exclude_undeliverable: boolean
  definition: Record<string, unknown>
  member_count?: number
  target_publications?: string[]
}

export type SyncRun = {
  id: number
  list_address: string
  segment_id: number | null
  sync_type: string
  delta_added: number
  delta_removed: number
  started_at: string
  completed_at: string | null
  status: string
  error: string | null
}

export type ExportStatus = {
  request: {
    id: number
    status: string
    requested_at: string | null
    picked_up_at: string | null
    completed_at: string | null
    error: string | null
  } | null
  last_export: {
    status: string
    filename: string | null
    row_count: number
    completed_at: string | null
    error: string | null
  } | null
}

export type WebhookAlert = {
  id: number
  source: string
  kind: string
  level: 'amber' | 'red'
  message: string
  details: Record<string, unknown>
  raised_at: string
  resolved_at: string | null
}

export type WebhookAlertsResponse = {
  active: WebhookAlert[]
  history: WebhookAlert[]
}

export type WebhookHealth = {
  sources: Record<string, {
    success: number
    failed: number
    signature_invalid: number
    duplicate: number
    pending: number
    total: number
    errors_1h: number
    last_arrival_at: string | null
    last_error_at: string | null
    last_error_type: string | null
    last_error: string | null
    failures_by_kind_24h?: {
      transient: number
      permanent: number
      unclassified: number
      unset: number
    }
  }>
}

export type ValidationCoverage = {
  total_with_email: number
  validated: number
  unvalidated: number
  stale: number
  needs_attention: number
  coverage: number
  by_status: Record<string, number>
  by_risk: Record<string, number>
  last_validated_at: string | null
  ttl_days: number
  sweep_limit_per_night: number
}

export type DeliverabilityRateBlock = {
  sent: number
  delivered: number
  bounced: number
  complaints: number
  unsubs: number
  bounce_rate: number
  complaint_rate: number
  unsub_rate: number
}

export type DeliverabilityCampaign = DeliverabilityRateBlock & {
  campaign_id: number
  name: string
}

export type Deliverability = {
  window_7d: DeliverabilityRateBlock
  window_30d: DeliverabilityRateBlock
  by_domain: Record<string, DeliverabilityRateBlock>
  by_title: Record<string, DeliverabilityRateBlock>
  by_staleness: Record<string, DeliverabilityRateBlock>
  by_campaign: DeliverabilityCampaign[]
}

// One cohort cell: total count + revenue, plus the renewed-so-far
// subset (cohort members whose live subscription has flipped back to
// Active during the cycle). Renewed.* stays at zero for forecast
// months since those cohorts haven't transitioned yet.
export type ExpirationPair = {
  count: number
  revenue: number
  renewed: { count: number; revenue: number }
}

export type FunnelBucket =
  | 'renewed'         // sub flipped Active OR ar_status moved → CHARGED this cycle
  | 'in_retry'        // ar_status moved INTO a non-terminal failure this cycle
  | 'terminal_fail'   // ar_status moved INTO FIFTH/FINAL CHARGE FAILED
  | 'ar_disabled'     // ar_status moved INTO an AR-off state
  | 'awaiting'        // no this-cycle change — still queued / stale
export type FunnelCells = Record<FunnelBucket, { count: number; revenue: number }>

export type DashboardSnapshot = {
  publications: Array<Publication & {
    active_count: number
    expiring_30d: number
    revenue_active: number
    revenue_active_remit: number
    auto_renew_count: number
    whitemail_count: number
    current_renewal_price: number | null
    current_breakdown: {
      auto_count: number
      auto_revenue: number
      manual_with_email_count: number
      manual_with_email_revenue: number
      manual_no_email_count: number
      manual_no_email_revenue: number
      agency_count: number
      agency_remit_revenue: number
    }
    forecast_12mo: {
      count: number
      gross: number
      remit: number
      auto_count: number
      auto_revenue: number
      manual_with_email_count: number
      manual_with_email_revenue: number
      manual_no_email_count: number
      manual_no_email_revenue: number
      agency_count: number
      agency_remit_revenue: number
    }
    last_sync: { at: string | null; added: number; removed: number; status: string | null }
  }>
  totals: {
    active: number             // active subscription rows (matches matrix)
    active_subscribers: number // distinct people
    suppressed: number
    events_24h: number
    revenue_active: number         // gross at list
    revenue_active_remit: number   // agency @ remit
    auto_renew_count: number
    auto_renew_charged: number
    auto_renew_failed: number
    whitemail_count: number
    no_email_count: number
    // 4-category breakdown of currently-active subs (same taxonomy as forecast)
    current_auto_count: number
    current_manual_with_email_count: number
    current_manual_no_email_count: number
    current_agency_count: number
    current_auto_revenue: number
    current_manual_with_email_revenue: number
    current_manual_no_email_revenue: number
    current_agency_remit_revenue: number
    pending_duplicates?: number
    tracks_queue_open?: number
    tracks_queue_overdue?: number
  }
  reachability: {
    by_publication: Array<{
      slug: string
      name: string
      auto_email: number
      auto_no_email: number
      manual_email: number
      manual_no_email: number
      total_active: number
    }>
    totals: {
      auto_email: number
      auto_no_email: number
      manual_email: number
      manual_no_email: number
      total_active: number
      total_no_email: number
      total_auto_renew: number
      total_manual: number
    }
  }
  payment_methods: {
    buckets: string[]
    totals: Record<string, number>
    grand_total: number
    by_publication: Array<{
      slug: string
      name: string
      counts: Record<string, number>
      total: number
    }>
  }
  this_month: {
    month_label: string
    by_publication: Array<{
      slug: string
      name: string
      new: number
      renewals: number
      expired: number
      net: number
    }>
    totals: { new: number; renewals: number; expired: number; net: number }
  }
  ar_cycle_effectiveness: {
    cohorts: Array<{
      cohort_month: string                      // 'May 2026'
      cohort_size: number                       // AR On members entering the cycle
      cohort_revenue: number                    // sum of list prices entering the cycle
      stages: Array<{
        attempt_seq: number                     // 1, 2, 3, 4, 5
        entered: number                         // total subs whose Nth attempt landed here
        entered_revenue: number                 // list-price total of stage entrants
        outcomes: Record<string, { count: number; revenue: number }>
      }>
      final_disposition: {
        renewed: { count: number; revenue: number }
        terminal_failed: { count: number; revenue: number }
        ar_disabled: { count: number; revenue: number }
        card_update_pending: { count: number; revenue: number }
        still_in_cycle: { count: number; revenue: number }
      }
      kpis: {
        first_attempt_success_pct: number
        retry_recovery_pct: number
        conversion_pct: number
        terminal_loss_pct: number
      }
    }>
  }
  ar_process_current_month: {
    cohort_month: string                            // e.g., "May 2026"
    cohort_total: { count: number; revenue: number }
    ar_statuses: Array<FunnelBucket>
    publications: Array<{ slug: string; name: string }>
    snapshot: FunnelCells
    snapshot_by_pub: Record<string, {
      by_status: FunnelCells
      total: { count: number; revenue: number }
    }>
    cohort_summary_by_pub: Record<string, {
      newly_expired: number
      agent: number
      ar_on: number
      ar_off_email: number
      ar_off_no_email: number
    }>
    cohort_summary_totals: {
      newly_expired: number
      agent: number
      ar_on: number
      ar_off_email: number
      ar_off_no_email: number
    }
    daily_progression: Array<{
      date: string                                  // ISO YYYY-MM-DD
      by_status: FunnelCells
      total: { count: number; revenue: number }
    }>
  }
  upcoming_expirations: {
    months: string[]
    publications: Array<{ slug: string; name: string }>
    current_prices: Record<string, number | null>
    cohorts: Array<'agent' | 'ar' | 'no_ar_email' | 'no_ar_no_email'>
    prior_cohorts: Array<{
      month: string
      by_pub: Record<string, {
        agent: ExpirationPair
        ar: ExpirationPair
        no_ar_email: ExpirationPair
        no_ar_no_email: ExpirationPair
      }>
      totals_by_cohort: {
        agent: ExpirationPair
        ar: ExpirationPair
        no_ar_email: ExpirationPair
        no_ar_no_email: ExpirationPair
      }
      total: { count: number; revenue: number }
    }>
    by_month: Array<{
      month: string
      by_pub: Record<string, {
        agent: ExpirationPair
        ar: ExpirationPair
        no_ar_email: ExpirationPair
        no_ar_no_email: ExpirationPair
      }>
      totals_by_cohort: {
        agent: ExpirationPair
        ar: ExpirationPair
        no_ar_email: ExpirationPair
        no_ar_no_email: ExpirationPair
      }
      total: { count: number; revenue: number }
    }>
    totals_by_pub: Record<string, {
      by_cohort: {
        agent: ExpirationPair
        ar: ExpirationPair
        no_ar_email: ExpirationPair
        no_ar_no_email: ExpirationPair
      }
      total: { count: number; revenue: number }
    }>
    grand_by_cohort: {
      agent: ExpirationPair
      ar: ExpirationPair
      no_ar_email: ExpirationPair
      no_ar_no_email: ExpirationPair
    }
    grand_total: { count: number; revenue: number }
  }
  sync_health: {
    by_publication: Array<{
      slug: string
      name: string
      latest_event_at: string | null
      events_24h: number
      events_7d: number
      last_failed_event_at: string | null
      tone: 'ok' | 'warn' | 'err'
      warn_hours: number
      err_hours: number
      threshold_source: 'autotuned' | 'default'
    }>
    csv: {
      last_import_at: string | null
      last_import_rows: number
      last_import_status: string | null
      last_import_filename: string | null
      last_import_breakdown: {
        rows_seen: number
        rows_imported: number
        subscribers_upserted: number
        subscriptions_upserted: number
        takeover_rows_protected: number
        rows_skipped_missing_email: number
        rows_skipped_bad_publication: number
        rows_skipped_other: number
        errors_count: number
        duration_seconds: number | null
      } | null
      tone: 'ok' | 'warn' | 'err'
      last_export_at?: string | null
      last_export_status?: string | null
    }
    totals: {
      webhook_errors_24h: number
      publications_stale_6h: number
      publications_stale_24h: number
    }
  }
  recent_events: WebhookEvent[]
  recent_syncs: SyncRun[]
}

export type EmailTemplate = {
  id: number
  slug: string
  name: string
  purpose: 'renewal' | 'welcome' | 'winback' | 'marketing' | 'transactional'
  publication_id: number | null
  subject: string
  preheader: string | null
  html_body: string
  text_body: string | null
  sample_vars: Record<string, unknown>
  variable_defaults: Record<string, unknown>
  enabled: boolean
  created_at: string | null
  updated_at: string | null
}

export type TemplatePreview = {
  subject: string
  html: string
  text: string | null
  merge_vars: Record<string, unknown>
  preview_email: string | null
  data_source: 'real_full' | 'real_subscriber_only' | 'fake' | 'sample'
  subscriber_id: number | null
  subscription_id: number | null
  personalized_links: boolean
}

export type FollowUpWave = {
  day_offset: number
  template_id: number | null
  subject: string | null
}

export type Campaign = {
  id: number
  slug: string
  name: string
  template_id: number
  segment_id: number
  additional_segment_ids: number[]
  schedule_type: 'one_shot' | 'recurring'
  scheduled_for: string | null
  recurring_cron: string | null
  recurring_dedup_days: number
  status: 'draft' | 'scheduled' | 'running' | 'completed' | 'cancelled' | 'failed'
  from_email: string | null
  reply_to: string | null
  last_fired_at: string | null
  next_fire_at: string | null
  last_run_sent_count: number
  last_run_error: string | null
  attribution_window_days: number
  mailgun_tags: string[]
  follow_up_waves: FollowUpWave[]
  created_at: string | null
  updated_at: string | null
  sends_total?: number
  status_breakdown?: Record<string, number>
  recent_sends?: CampaignSend[]
  // Attached by GET /api/campaigns only (cheap per-row aggregation). Not
  // computed on GET /api/campaigns/:id — use CampaignResults for full detail.
  conversions_count?: number
  conversions_revenue?: number
}

// ============== AR Report Card ==============

export type ARBucket = 'renewed' | 'pending' | 'in_retry' | 'terminal_fail' | 'awaiting' | 'ar_disabled'
export type ARLifecycleState = 'enrolling' | 'in_progress' | 'settling' | 'settled'
export type ARGrade = 'A' | 'B' | 'C' | 'D' | '—'
export type ARFailureCategory =
  | 'invalid_card_closed' | 'invalid_card_expired'
  | 'no_payment_profile' | 'card_type_unsupported'
  | 'insufficient_funds' | 'declined' | 'avs_mismatch' | 'system_error' | 'other'
  // Legacy: kept for deserialization until we re-deploy with the split.
  | 'invalid_card'
export type ARCardHealth = 'healthy' | 'transient_decline' | 'chronic_failure' | 'multi_cycle_failure'
export type ARRetryPath = 'fast' | 'standard' | null

export type ARSummaryCell = {
  cohort_month: string         // YYYY-MM-01
  publication_slug: string
  publication_name: string
  cohort_size: number
  buckets: Record<ARBucket, { count: number; revenue: number }>
  renewed_pct: number
  revenue_captured: number
  revenue_at_risk: number
  lifecycle_state: ARLifecycleState
  grade: ARGrade
}

export type ARSummary = {
  months_requested: number
  cohort_months_requested: string[]
  cohort_months_with_data: string[]
  publications: Array<{ slug: string; name: string }>
  cells: ARSummaryCell[]
  kpis: {
    total_cohort_size: number
    total_renewed: number
    total_in_retry: number
    total_pending: number
    total_terminal_fail: number
    renewed_pct: number
    revenue_captured: number
    revenue_at_risk: number
  }
}

export type ARStage = {
  attempt_seq: number
  outcomes: Record<string, { count: number; revenue: number }>
  entered: number
  entered_revenue: number
  by_retry_path: { fast: number; standard: number; null: number }
  by_failure_category: Partial<Record<ARFailureCategory, number>>
}

export type ARCohortDetail = {
  cohort_month: string
  publication_slug: string
  publication_name: string
  cohort_size: number
  cohort_revenue: number
  lifecycle_state: ARLifecycleState
  grade: ARGrade
  stages: ARStage[]
  // Distinct SUBSCRIBERS per category. SC retries 5x regardless, so counting
  // attempt rows inflated every category ~4-5x and read as members.
  failure_categories: Partial<Record<ARFailureCategory, number>>
  // Attempt rows per category — how much retry effort a category burns.
  failure_category_attempts: Partial<Record<ARFailureCategory, number>>
  // Categories where a retry can never succeed. Sent by the API so the panel
  // and the classifier cannot drift on which those are.
  failure_categories_unrecoverable: ARFailureCategory[]
  // count = attempt rows, subscribers = distinct members. Both are shown:
  // this panel sits beneath the failure breakdown, which counts members.
  top_failure_details: Array<{ details: string; count: number; subscribers: number }>
  final_disposition: Record<ARBucket, { count: number; revenue: number }>
  retry_path_totals: { fast: number; standard: number; null: number }
  kpis: {
    first_attempt_success_pct: number
    retry_recovery_pct: number
    renewed_pct: number
    terminal_loss_pct: number
    fast_retry_a1_count: number
    fast_retry_recovered_count: number
    fast_retry_recovery_pct: number
    fast_retry_recovered_revenue: number
  }
}

export type ARTimelineDay = {
  date: string
  day_offset: number
  total: number
  by_status: Record<string, number>
  by_retry_path: { fast: number; standard: number; null: number }
  by_attempt_seq: Record<string, number>
  wave_label: string | null
}

export type ARCohortTimeline = {
  cohort_month: string
  publication_slug: string
  publication_name: string
  cohort_size: number
  days: ARTimelineDay[]
}

export type ARAttemptListItem = {
  attempt_id: number
  subscription_id: number | null
  simplecirc_account_id: string | null
  email: string | null
  first_name: string | null
  last_name: string | null
  attempt_at: string | null
  attempt_seq: number
  status: string
  retry_path: 'fast' | 'standard' | null
  failure_category: ARFailureCategory | null
  details: string | null
  paid_amount: number | null
}

export type ARAttemptList = {
  cohort_month: string
  publication_slug: string
  items: ARAttemptListItem[]
  total: number
  returned: number
  offset: number
  limit: number
}

export type ARAttemptDetail = {
  attempt_id: number
  subscription_id: number | null
  simplecirc_account_id: string
  publication_slug: string
  attempt_at: string | null
  status: string
  retry_path: 'fast' | 'standard' | null
  failure_category: ARFailureCategory | null
  details: string | null
  paid_amount: number | null
  raw_row: Record<string, unknown>
  subscription: {
    id: number
    publication_id: number
    status: string
    auto_renew: boolean
    auto_renew_status: string | null
    current_period_end: string | null
  } | null
  subscriber: {
    id: number
    simplecirc_account_id: string | null
    email: string | null
    first_name: string | null
    last_name: string | null
  } | null
  surrounding_attempts: Array<{
    attempt_id: number
    attempt_seq: number
    attempt_at: string | null
    status: string
    retry_path: 'fast' | 'standard' | null
    failure_category: ARFailureCategory | null
    details: string | null
    paid_amount: number | null
    is_focus: boolean
  }>
  time_since_prior_hours: number | null
  time_to_next_hours: number | null
}

export type ARGlossaryEntry = {
  label: string
  short: string
  detail: string
  color?: string
  is_failure?: boolean
  is_terminal?: boolean
  retry_path?: 'fast' | 'standard'
  recovery?: string
  trigger_status?: string
}

export type ARGlossary = {
  statuses: Record<string, ARGlossaryEntry>
  failure_categories: Record<string, ARGlossaryEntry>
  retry_paths: Record<string, ARGlossaryEntry>
  lifecycle_states: Record<string, ARGlossaryEntry>
  buckets: Record<string, ARGlossaryEntry>
  card_health: Record<string, ARGlossaryEntry>
  kpis: Record<string, ARGlossaryEntry>
  schedule: {
    label: string
    anchor_note: string
    events: Array<{
      sc_day: number
      cohort_offset: number
      label: string
      detail: string
    }>
  }
}

export type ARBucketRow = {
  subscription_id: number
  subscriber_id: number | null
  simplecirc_account_id: string | null
  email: string | null
  first_name: string | null
  last_name: string | null
  subscription_status: string
  attempts_count: number
  failures_count: number
  latest_status: string | null
  latest_attempt_at: string | null
  latest_failure_category: ARFailureCategory | null
  latest_details: string | null
  first_attempt_retry_path: 'fast' | 'standard' | null
  card_health: ARCardHealth
  days_in_state: number
  revenue_at_risk: number
}

export type ARBucketResponse = {
  cohort_month: string
  publication_slug: string
  bucket: ARBucket
  total_in_bucket: number
  returned: number
  items: ARBucketRow[]
}

export type ARSubscriberHistory = {
  subscriber_id: number
  simplecirc_account_id: string | null
  email: string | null
  first_name: string | null
  last_name: string | null
  card_health: ARCardHealth
  subscriptions: Array<{
    id: number
    publication_id: number
    status: string
    auto_renew: boolean
    current_period_end: string | null
  }>
  attempts: Array<{
    attempt_at: string
    subscription_id: number
    publication_slug: string
    status: string
    failure_category: ARFailureCategory | null
    paid_amount: number | null
    details: string | null
  }>
  cohort_entries: Array<{
    cohort_month: string
    publication_slug: string
    is_ar: boolean
    is_agent: boolean
    has_email: boolean
    entry_ar_status: string | null
    joined_on: string | null
  }>
  recovery_emails: Array<{
    send_id: number
    template_slug: string
    subject: string | null
    sent_at: string | null
    status: string
    campaign_id: number | null
  }>
}

export type TestPool = {
  id: number
  slug: string
  name: string
  description: string | null
  member_count: number
  created_at: string | null
  updated_at: string | null
}

export type TestPoolMember = {
  subscriber_id: number
  email: string | null
  first_name: string | null
  last_name: string | null
  simplecirc_account_id: string | null
  publication_slugs: string[]
  added_at: string | null
}

export type TestPoolDetail = TestPool & {
  members: TestPoolMember[]
}

export type TemplateCoverageReport = {
  template_slug: string
  total_recipients: number
  passes: boolean
  variables: Array<{
    variable: string
    populated: number
    total: number
    coverage: number
    known_source: boolean
    tone: 'ok' | 'warn' | 'fail' | 'unknown'
    has_default: boolean
    default_value: string | null
  }>
  unknown_variables: string[]
  hard_block_variables: string[]
}

export type SegmentValidationReport = {
  total: number
  validated_count: number
  cached_count: number
  unvalidated_count: number
  api_errors: number
  coverage: number
  by_status: Record<string, number>
  by_risk: Record<string, number>
  role_addresses: number
  disposable_addresses: number
  did_you_mean_suggestions: Array<{ address: string; suggestion: string }>
  undeliverable_count: number
  undeliverable_rate: number
  high_risk_count: number
  net_sendable: number
}

export type CampaignResults = {
  campaign: Campaign
  sends_total: number
  status_breakdown: Record<string, number>
  engagement: {
    unique_openers: number
    unique_clickers: number
    total_clicks: number
  }
  top_links: Array<{
    url: string
    clicks: number
    unique_clickers: number
  }>
  conversions: {
    window_days: number
    fire_at: string | null
    cohort_count: number
    clickthrough_count: number
    cohort_revenue: number
    orders: Array<{
      simplecirc_order_id: string
      subscriber_id: number
      order_date: string | null
      amount_paid: number | null
      new_or_renewal: string | null
      had_click: boolean
    }>
  }
  recipients: Array<{
    id: number
    subscriber_id: number | null
    to_email: string
    status: string
    sent_at: string | null
    failure_reason: string | null
    click_count: number
    last_event: { event: string; at: string } | null
    renewed_at: string | null
    renewal_order_id: string | null
  }>
  recipients_truncated: boolean
  waves: CampaignWaveBreakdown[]
}

export type CampaignWaveBreakdown = {
  day_offset: number | null   // null = initial Day-1 send
  label: string                // "Initial" or "+1 day"
  fired_at: string | null
  scheduled_at?: string | null // present for non-initial; ISO datetime
  sent_count: number
  delivered: number
  opened: number
  clicked: number
  status_breakdown: Record<string, number>
}

export type CampaignSend = {
  id: number
  subscriber_id: number
  to_email: string
  subject: string | null
  status: string
  sent_at: string | null
  failure_reason: string | null
  mailgun_message_id: string | null
  last_event: { event: string; at: string } | null
  event_count: number
}

export type CampaignEstimate = {
  recipient_count: number
  from_email: string
  domain: string
  sample: {
    to: string
    subject: string
    html: string
    text: string | null
  } | { error: string } | null
}

export type SubscriberOrder = {
  id: number
  simplecirc_order_id: string
  order_date: string | null
  kind: 'payment' | 'refund' | 'void'
  amount_paid: number | null
  payment_type: string | null
  payment_source: string | null
  agency_name: string | null
  payment_remit_amount: number | null
  new_or_renewal: string | null
  promo_code: string | null
  // The card that paid, as it was at charge time. Stripe and Authorize.Net
  // orders carry this; SimpleCirc ones don't (their card is in the gateway ledger).
  card_type: string | null
  last4: string | null
  card_expiry: string | null      // 'YYYY-MM'
  raw_source: string
  payer_full_name: string | null
  paid_by_other: boolean
}

export type SubscriberOrdersResponse = {
  subscriber_id: number
  lifetime_paid: number
  lifetime_refunded: number
  lifetime_net: number
  first_order_date: string | null
  last_order_date: string | null
  total_orders: number
  groups: Array<{
    publication_slug: string
    publication_name: string
    subtotal_paid: number
    subtotal_refunded: number
    order_count: number
    orders: SubscriberOrder[]
  }>
}

export type ReconciliationReport = {
  id: number
  generated_at: string
  prev_import_id: number | null
  current_import_id: number
  prev_filename: string | null
  current_filename: string
  prev_rows: number | null
  current_rows: number
  net_delta: number
  totals: {
    new: number
    gone: number
    unchanged: number
    status_transitions: Record<string, number>
    auto_renew_on: number
    auto_renew_off: number
    expiry_changed: number
    issues_changed: number
    payment_changed: number
  }
  by_publication: Record<string, {
    new: number
    gone: number
    unchanged: number
    status_transitions: Record<string, number>
    auto_renew_on: number
    auto_renew_off: number
    expiry_changed: number
    issues_changed: number
    payment_changed: number
  }>
  error: string | null
}

export type SegmentPreview = {
  count: number
  sample: Array<Pick<Subscriber, 'email' | 'first_name' | 'last_name'>>
  ran_in_ms: number
}

export type MeResponse = { email: string; role: string; sections: import('../lib/sections').SectionLink[]; can_view_member_activity?: boolean; preferences: { editorial_theme: 'light' | 'dark' | 'system' }; public_base_urls?: Record<string, string>; desk_tabs?: string[] }

export type TitleDetail = {
  slug: string
  name: string
  current_renewal_price: number
  mailgun_list_address: string | null
  wp_site_url: string | null
  active_count: number
  auto_renew_count: number
  manual_count: number
  auto_renew_with_email: number
  auto_renew_no_email: number
  manual_with_email: number
  manual_no_email: number
  agency_total: number
  agency_remit_total: number
  annual_run_rate: number
  by_month: Array<{
    month: string
    auto_renew_count: number
    auto_renew_revenue: number
    manual_with_email_count: number
    manual_with_email_revenue: number
    manual_no_email_count: number
    manual_no_email_revenue: number
    agency_count: number
    agency_revenue_prevailing: number
    agency_revenue_remit: number
    total_count: number
    total_revenue: number
  }>
  totals_12mo: {
    auto_renew_count: number
    auto_renew_revenue: number
    manual_with_email_count: number
    manual_with_email_revenue: number
    manual_no_email_count: number
    manual_no_email_revenue: number
    agency_count: number
    agency_revenue_prevailing: number
    agency_revenue_remit: number
    total_count: number
    total_revenue: number
  }
  out_of_window_count: number
}

export type AggregateDetail = Omit<
  TitleDetail,
  'slug' | 'name' | 'current_renewal_price' | 'mailgun_list_address' | 'wp_site_url'
> & {
  publications: Array<{
    slug: string
    name: string
    current_renewal_price: number | null
  }>
  agency_total: number
  agency_remit_total: number
  annual_run_rate_gross: number
  annual_run_rate_remit: number
}

// ---------- Duplicate-subscriber subsystem ----------

export type DuplicateReasonCode = {
  feature: string
  kind: 'exact' | 'fuzzy'
  weight: number
  detail: string
}

export type DuplicateSubscriberSummary = {
  id: number
  simplecirc_account_id: string
  email: string | null
  first_name: string | null
  last_name: string | null
  city: string | null
  state: string | null
  zipcode: string | null
  is_merged: boolean
  // ISO datetime — COALESCE(min(order_events.order_date),
  // min(subscriptions.started_at), subscribers.created_at).
  // The survivorship rule's account-age tiebreaker runs on this value.
  member_since: string | null
}

export type DuplicateTier = 'auto_merge' | 'review_high' | 'review_med' | 'ignore'

// One title on which one account of the pair paid for a gift subscription
// on the other. A pair that gifted both ways carries one link each way.
export type DuplicateGiftLink = {
  giver_id: number
  receiver_id: number
  publication_slug: string
  orders: number
  last_order_date: string | null
}

export type DuplicateCandidate = {
  id: number
  score: number
  tier: DuplicateTier
  source: 'batch' | 'ingest_hook'
  status: 'pending' | 'auto_merged' | 'confirmed_merged' | 'dismissed' | 'kept_related'
  detected_at: string
  resolved_at: string | null
  resolved_by: string | null
  reason_codes: DuplicateReasonCode[]
  a: DuplicateSubscriberSummary | null
  b: DuplicateSubscriberSummary | null
  gift_links: DuplicateGiftLink[]
}

export type DuplicateAutoMergedItem = {
  audit_id: number
  performed_at: string
  undo_deadline: string
  undoable: boolean
  winner_id: number
  loser_id: number
  winner_summary: DuplicateSubscriberSummary | null
  loser_summary: {
    id: number
    simplecirc_account_id: string | null
    email: string | null
    first_name: string | null
    last_name: string | null
  }
}

export type DuplicateListResponse = {
  tab: 'needs_review' | 'auto_merged' | 'dismissed'
  total: number
  tier_counts: { auto_merge: number; review_high: number; review_med: number }
  items: DuplicateCandidate[] | DuplicateAutoMergedItem[]
}

export type ApplyAutoMergesResponse = {
  applied: number
  skipped_not_auto_merge_tier: number
  skipped_disqualified: number
  failed: number
  errors: Array<{ candidate_id: number; error: string }>
}

export type DuplicateSubscriberDetail = {
  id: number
  simplecirc_account_id: string
  email: string | null
  first_name: string | null
  last_name: string | null
  phone: string | null
  address_1: string | null
  address_2: string | null
  city: string | null
  state: string | null
  zipcode: string | null
  country: string | null
  created_at: string | null
  last_synced_at: string | null
  member_since: string | null
  is_merged: boolean
  subscriptions: Array<{
    publication_slug: string
    publication_name: string
    status: string
    auto_renew: boolean
    current_period_end: string | null
    simplecirc_subscription_id: string
  }>
}

export type DuplicateDetailResponse = {
  candidate: DuplicateCandidate
  recommended_winner_id: number | null
  a: DuplicateSubscriberDetail
  b: DuplicateSubscriberDetail
}

export type UpstreamCleanupItem = {
  id: number
  added_at: string
  cleared_at: string | null
  cleared_by: string | null
  reason: string | null
  notes: string | null
  loser_simplecirc_account_id: string
  canonical: {
    id: number
    simplecirc_account_id: string | null
    email: string | null
    first_name: string | null
    last_name: string | null
  }
}

// ---------- Admin members support tooling (Track A · Phase A4) ----------
//
// These types mirror the response models in
// aamcirc/admin/routers/members_admin.py. The backend was built in
// Phase A2; the UI surface ships in A4.

export type EntitlementSnapshot = {
  publication_slug: string
  entitled: boolean
  reason: string
  expires_at: string | null
  grace_until: string | null
  notes: string | null
  /** Subscription the verdict came from; null for grant-derived rows. */
  subscription_id: number | null
  subscription_source: string | null
  /** False for SimpleCirc-sourced rows — SC owns those dates. */
  expiry_editable: boolean
}

export type AttemptCounts = {
  login_24h: number
  claim_24h: number
  reset_24h: number
  magic_link_24h: number
  /** Wrong sign-in codes (Home Screen apps); successes are not counted. */
  sign_in_code_24h: number
  failed_24h: number
}

export type TempAccessGrantRow = {
  id: number
  publication_slug: string
  granted_by_user_id: number
  granted_at: string
  expires_at: string | null
  reason: string
  revoked_at: string | null
  revoked_by_user_id: number | null
  revoked_reason: string | null
  is_active: boolean
}

export type MemberAdminDetail = {
  id: number
  simplecirc_account_id: string
  email: string | null
  first_name: string | null
  last_name: string | null
  zipcode: string | null
  claimed_at: string | null
  email_verified_at: string | null
  password_set_at: string | null
  has_password: boolean
  merged_into_id: number | null
  aliases: string[]
  last_synced_at: string | null
  entitlements: EntitlementSnapshot[]
  auth_attempts_24h: AttemptCounts
  temp_access_grants: TempAccessGrantRow[]
}

export type AuditEntry = {
  id: number
  event: string
  ip: string | null
  user_agent: string | null
  details: Record<string, unknown>
  created_at: string
}

export type AuditResponse = {
  subscriber_id: number
  entries: AuditEntry[]
}

export type AccountChangeCategory = 'profile' | 'address' | 'billing' | 'auto_renew' | 'status'

export type AccountChangeEntry = {
  kind: 'change' | 'action'
  at: string
  category: AccountChangeCategory
  field: string
  label: string
  old_value: string | null
  new_value: string | null
  publication_slug: string | null
  source: string
  source_label: string
  actor: string | null
  details: Record<string, unknown>
}

export type AccountChangesResponse = {
  subscriber_id: number
  entries: AccountChangeEntry[]
}

export type RecentReadEntry = {
  article_id: number
  article_slug: string | null
  article_title: string | null
  publication_slug: string | null
  channel: string
  event_type: string
  created_at: string
}

export type RecentReadsResponse = {
  subscriber_id: number
  entries: RecentReadEntry[]
}

// ---------- Renewals — card on file, attempt history, charge now (Task 10) ----------

export type RenewalAttemptRow = {
  id: number
  attempt_no: number
  scheduled_for: string
  status: string
  amount_cents: number
  decline_code: string | null
  error: string | null
  gateway_charge_id: string | null
  triggered_by: string
  created_at: string
  completed_at: string | null
}

export type CardOnFile = {
  brand: string | null
  last4: string | null
  expiry: string | null
}

export type RenewalSubscription = {
  subscription_id: number
  publication_slug: string
  /** 'stripe' | 'authnet' (our engine charges the vaulted card) or 'paypal' (PayPal bills it). */
  billing_provider: string
  paypal_subscription_id: string | null
  billing_plan: string | null
  auto_renew: boolean
  do_not_renew: boolean
  current_period_end: string | null
  /** SimpleCirc's imported price — history, not what we charge next. */
  auto_renew_price: number | null
  /** What the engine (and Charge now) would charge; null for PayPal rows. */
  next_charge_cents: number | null
  auto_renew_status: string | null
  card_on_file: CardOnFile | null
  /** SimpleCirc still bills this row: listed, but every action is refused. */
  sc_managed: boolean
  /** Other memberships billed by the SAME PayPal agreement as this row —
   *  a BOGO pair. Empty for every ordinary row. PayPal has no per-row
   *  cancel: stopping auto-renew here ends every membership named here too. */
  bogo_partner_memberships: string[]
  /** 'active' | 'on_hold' | 'cancelled' | 'expired' … */
  status: string
  /** Cancelled or expired with the paid-through date behind us: nothing
   *  will renew, so the panel offers no charge, card link or renewal date. */
  membership_ended: boolean
  /** Set while a renewal is failing and we are still retrying it. */
  retry: RetrySummary | null
  attempts: RenewalAttemptRow[]
}

export type RetrySummary = {
  /** The member keeps reading through this day, the cycle's last attempt day. */
  access_until: string
  /** Next scheduled try; null while waiting for a new card or an open attempt. */
  next_retry_on: string | null
  /** A hard decline (lost, stolen, closed, expired card): no retry until the card changes. */
  waiting_for_new_card: boolean
  hard_decline_code: string | null
}

export type RenewalsResponse = {
  subscriptions: RenewalSubscription[]
}

export function getMemberRenewals(subscriberId: number) {
  return api.get<RenewalsResponse>(`/api/admin/members/${subscriberId}/renewals`)
}

export function chargeRenewalNow(subscriberId: number, subscriptionId: number) {
  return api.post<RenewalAttemptRow>(
    `/api/admin/members/${subscriberId}/renewals/${subscriptionId}/charge-now`,
  )
}

export function sendCardUpdateLink(subscriberId: number, subscriptionId: number) {
  return api.post<{ sent: boolean }>(
    `/api/admin/members/${subscriberId}/renewals/${subscriptionId}/send-card-link`,
  )
}

export type StopAutoRenewResponse = {
  subscription_id: number
  auto_renew: boolean
  do_not_renew: boolean
  status: string
  current_period_end: string | null
}

/** Stop billing; the member keeps access to current_period_end. A PayPal
 *  row is cancelled at PayPal first and only marked once PayPal agrees. */
export function stopAutoRenew(subscriberId: number, subscriptionId: number, reason: string) {
  return api.post<StopAutoRenewResponse>(
    `/api/admin/members/${subscriberId}/renewals/${subscriptionId}/stop-auto-renew`,
    { reason },
  )
}

// ============== Yesterday's Orders ==============

export type YesterdayOrderRow = {
  simplecirc_order_id: string
  order_date: string | null
  payer_full_name: string | null
  publication_name: string | null
  new_or_renewal: string
  amount_paid: number
}

export type YesterdayOrders = {
  window: { label: string; tz: string; start_utc: string; end_utc: string }
  as_of: string | null
  order_count: number
  new: number
  renewal: number
  unknown: number
  gross_collected: number
  refund_count: number
  refund_amount: number
  void_count: number
  multi_line_note: boolean
  by_publication: Array<{
    publication_slug: string
    publication_name: string
    count: number
    gross: number
  }>
  orders: YesterdayOrderRow[]
}

// ---------- Site orders (our own checkout) ----------

export type SiteOrder = {
  id: number
  order_id: string
  source: string
  order_date: string | null
  kind: string
  payer_full_name: string | null
  payer_email: string | null
  /** The member the line is for — on a gift, the RECIPIENT. */
  subscriber_id: number | null
  /** Who paid: what the Buyer column links to. Null on a gift whose
   *  buyer can't be resolved (never falls back to the recipient). */
  payer_subscriber_id: number | null
  /** On a gift, who received it; null otherwise. */
  gift_recipient_name: string | null
  publication_name: string | null
  publication_slug: string | null
  new_or_renewal: string | null
  term_months: number | null
  amount_paid: number
  promo_code: string | null
  card_type: string | null
  last4: string | null
}

export type SiteOrderList = {
  items: SiteOrder[]
  total: number
  order_count: number
  gross_collected: number
  refund_count: number
  refund_amount: number
}

export type SiteOrderLine = {
  id: number
  publication_name: string | null
  publication_slug: string | null
  amount_paid: number | null
  new_or_renewal: string | null
  term_months: number | null
  subscriber_id: number | null
  subscription_id: number | null
  subscription_type_code: string | null
  gift_recipient_account_id: string | null
  /** Whose membership this line is — the RECIPIENT on a gift, not the
   *  payer on the header. `access_ends_if_voided` is computed from this
   *  person, so this is the name a void confirmation must use. */
  subscriber_name: string | null
  /** Where the membership's expiry lands if this line's order is voided,
   *  or null when there is no live membership or no recorded term to
   *  reverse. */
  access_ends_if_voided: string | null
}

/** A refund check we wrote against a check order.
 *
 *  Not a `Refund`: no gateway held the money, so there is no provider
 *  refund with a state to track. It is booked as an ordinary
 *  `{order_key}-REFUND-{check number}` order and read back here. */
export type OfflineRefund = {
  order_id: string
  order_date: string | null
  amount: number | null
  refund_check_number: string | null
  reason: string | null
}

export type Refund = {
  id: number
  order_id: number
  amount: number
  status: string
  provider: string
  idempotency_key: string
  provider_refund_id: string | null
  cancel_billing: boolean
  cancel_subscription: boolean
  error: string | null
  reason: string | null
  requested_by: number | null
  created_at: string | null
  completed_at: string | null
  /** The gateway cancelled the charge before it settled — no money moved. */
  voided: boolean
}

export type SiteOrderDetail = {
  id: number
  order_id: string
  source: string
  order_date: string | null
  kind: string
  amount_paid: number | null
  amount_due: number | null
  payment_type: string | null
  payment_source: string | null
  promo_code: string | null
  card_type: string | null
  last4: string | null
  card_expiry: string | null
  payer_full_name: string | null
  payer_email: string | null
  payer_account_id: string | null
  gift_giver_account_id: string | null
  gift_message: string | null
  utm: {
    source: string | null
    medium: string | null
    campaign: string | null
    term: string | null
    content: string | null
  }
  ingested_at: string | null
  raw_payload: Record<string, unknown> | null
  lines: SiteOrderLine[]
  /** null when the provider could not be reached, not when it is zero. */
  refundable_amount: number | null
  refunds: Refund[]
  /** True once a `{order_id}-VOID` order exists for this one. */
  voided: boolean
  /** Refund checks written against this one. Always empty for a card
   *  order, which uses `refunds` instead. */
  offline_refunds: OfflineRefund[]
  /** On a refund order only: the payment it reverses. */
  refunded_order: { id: number; order_id: string; voided: boolean } | null
}

// ---------- Mailings (direct mail) ----------

export type Mailing = {
  id: number
  slug: string
  name: string
  publication_slug: string | null
  publication_name: string | null
  kind: string
  mail_date: string
  attribution_window_days: number
  status: 'planned' | 'mailed' | 'closed'
  source_file: string | null
  notes: string | null
  recipient_count: number
  matched_count: number
  conversions_count: number
  conversions_revenue: number
  created_at: string | null
  updated_at: string | null
}

export type MailingRecipientRow = {
  account_number: string
  subscriber_id: number | null
  matched: boolean
  converted: boolean
  expired_on: string | null
  payment_type: string | null
  payment_class: 'online' | 'check' | 'other' | null
  name: string | null
  renewal_link: string | null
}

export type MailingMonthBucket = {
  month: string | null
  recipients: number
  conversions: number
  revenue: number
  online: number
  check: number
  other: number
}

export type MailingDetail = Mailing & {
  new_or_renewal: { new: number; renewal: number; unknown: number }
  month_breakdown: MailingMonthBucket[]
  recipients: MailingRecipientRow[]
}

// ---------- Authorize.Net gateway ledger ----------
// `gateway_transactions` mirrors what the gateway actually did. It is the only
// place refunds, the card-at-charge-time, and the issuer's own decline reason
// exist — SimpleCirc's AR export carries none of them.

export type GatewayFlag =
  | 'held_and_expired'
  | 'lost_or_stolen'
  | 'card_swapped'
  | 'refunded'
  | 'cvv_mismatch'

/** covered = we hold transactions. no_transactions = we hold a gateway identity
 *  and it was quiet. no_gateway_identity = we hold no key at all. The last two
 *  must never be rendered the same way. */
export type GatewayCoverage = 'covered' | 'no_transactions' | 'no_gateway_identity'

export type GatewayTransactionRow = {
  trans_id: string
  batch_id: string | null
  submitted_at: string | null
  settled_at: string | null
  transaction_type: string | null
  transaction_status: string | null
  auth_amount: number | null
  settle_amount: number | null
  /** Signed. Total this, never settle_amount — the gateway reports refunds positive. */
  net_amount: number | null
  response_reason_code: number | null
  response_reason_description: string | null
  card_type: string | null
  last4: string | null
  /** The three gateway identifiers, per charge. merchant_customer_id is what
   *  the gateway recorded at charge time and can differ from the
   *  subscription's stored key — that difference is how a stale key shows up. */
  customer_profile_id: string | null
  payment_profile_id: string | null
  merchant_customer_id: string | null
}

/** The nightly sweep's reading of the card on file. Card fields null with
 *  missing_at null means the profile exists and CAU removed its card. */
export type StoredProfile = {
  customer_profile_id: string
  payment_profile_id: string | null
  merchant_customer_id: string | null
  card_type: string | null
  last4: string | null
  /** checking | savings | businessChecking for a bank (eCheck) profile, else
   *  null. A bank profile's `expiry` is null too — nothing to expire, not an
   *  unread card — so render on this field, never by inferring from a
   *  missing expiry. */
  account_type: string | null
  expiry: string | null
  valid_through: string | null
  expires_before_renewal: boolean | null
  checked_at: string | null
  missing_at: string | null
}

export type GatewayGroupSummary = {
  flags: GatewayFlag[]
  gross: number
  refunded: number
  net: number
  /** Authorizations held for merchant review that expired uncaptured. */
  uncaptured: number
}

export type GatewayLedgerGroup = {
  subscription_id: number
  publication_slug: string
  publication_name: string
  gateway_customer_id: string | null
  /** Straight off the subscription. A purchase made here carries both from
   *  the moment it is charged, which is well before — often instead of —
   *  anything sweeping the profile into `stored_profile`. */
  gateway_profile_id: string | null
  gateway_payment_profile_id: string | null
  coverage: GatewayCoverage
  stored_transaction_id: string | null
  current_period_end: string | null
  stored_profile: StoredProfile | null
  summary: GatewayGroupSummary
  transactions: GatewayTransactionRow[]
}

export type GatewayLedger = {
  subscriber_id: number
  ledger_window_start: string | null
  card_check_available: boolean
  groups: GatewayLedgerGroup[]
}

export type CardOnFileState = 'no_profile' | 'no_card_on_file' | 'card'

/** Why nothing resolved. Absence of a card is never claimed — it is not
 *  provable from the identifiers we hold. */
export type CardOnFileReason = 'profile_deleted' | 'unresolved'

export type CardOnFileResult = {
  state: CardOnFileState
  reason: CardOnFileReason | null
  checked_at: string
  /** Which identifier actually resolved the profile; null when none did. */
  resolved_by: 'merchant_customer_id' | 'customer_profile_id' | 'renewal_barcode' | null
  /** Every identifier attempted, so "no profile" stays a claim about our keys
   *  rather than about the member. */
  identifiers_tried: string[]
  customer_profile_id: string | null
  card_type: string | null
  last4: string | null
  /** checking | savings | businessChecking for a bank (eCheck) profile, else
   *  null. A bank profile's `expiry` is null too — nothing to expire, not a
   *  masked one — so render on this field, never by inferring it from a
   *  missing expiry. */
  account_type: string | null
  /** 'YYYY-MM', or null when the gateway masked it. */
  expiry: string | null
  /** null means unknowable, which is not the same claim as "not expiring". */
  expires_before_renewal: boolean | null
}

// ---------- Flight Line agent drafts (/api/lab/section-drafts) ----------
export type SectionDraftStatus = 'draft' | 'needs_attention' | 'approved' | 'published' | 'unpublished' | 'rejected' | 'failed' | 'sources_ready' | 'queued' | 'superseded'
export type SectionDraftSummary = {
  id: number; publication_slug: string; status: SectionDraftStatus; grounding: 'corpus' | 'web'
  topic: { label?: string; search_intent?: string; representative_query?: string; queries?: string[]; impressions?: number; score?: number }
  origin?: 'idea' | 'import' | 'version' | 'edit'
  title: string | null; slug: string | null; created_at: string; reviewed_at: string | null; published_at: string | null
  wp_post_id: number | null; unsupported_ratio: number; word_count: number
  brief: Brief
  revised_from_id?: number | null; revision_note?: string | null
  destination?: 'section' | 'membership' | 'both'; member_wp_post_id?: number | null
}
export type SocialPost = {
  platform: string
  label: string
  status: string
  body: string
  length: number
  limit: number
  over_by: number
  link_url: string | null
  permalink: string | null
  image_url: string | null
  ambiguous: boolean
  last_error: string | null
  published_at: string | null
  publishable: boolean
  blocked_reason: string | null
}
export type SocialOutcome = {
  platform: string
  state: string
  detail: string
}
export type Suggestion = {
  id: string; kind: 'typo' | 'caption'; before: string; after: string; context: string
  status: 'open' | 'accepted' | 'dismissed'; asset_id?: number
  /** A caption's photo (its CDN src), stored beside asset_id when the draft is made. */
  src?: string
}
export type SectionDraftDetail = SectionDraftSummary & {
  suggestions?: Suggestion[]
  import_info?: { id: number; file_names: string[]; created_by: string; created_at: string; warnings: string[] } | null
  digital_edition_id?: number | null
  seo_title: string | null; meta_description: string | null; body_html: string | null
  byline?: string | null
  made_from?: { kind: 'draft' | 'article'; id: number; title: string; article_id?: number | null } | null
  source_changed_at?: string | null
  research_questions?: string[]
  go_deeper: { article_id: number | null; slug: string | null; title: string; kind?: string; edition_id?: number | null }[]
  sources: { id: string; kind: 'member' | 'public' | 'web' | 'issue' | 'draft'; title: string; url?: string | null; slug?: string | null; issue_label?: string | null; reference_only?: boolean; edition_id?: number | null; article_id?: number | null; passages: string[] }[]
  verification: { claims?: { text: string; supported: boolean; source_id?: string | null; note?: string | null }[]; removed?: string[]; not_found?: string[]; unsupported_ratio?: number; attention?: string[] }
  featured_image_url: string | null; featured_caption: string | null
  featured_caption_source: 'editor' | 'wp' | 'expanded' | 'inherited' | 'archive' | 'uncredited' | 'none' | null
  featured_photo_article: { article_id: number; title: string | null; slug: string | null } | null
  ai_model: string | null
  requested_by: string | null
  superseded_by_id?: number | null
  reviewed_by: string | null; review_note: string | null; error: string | null; public_url: string | null
  social: SocialPost[]
  social_config: string[]
  /** False in dry run or with no account connected: publish and publish-all answer 409. */
  social_posting_on: boolean
  orphaned_posts: string[]
}
export type SectionPhotoOption = {
  asset_id: number
  image_url: string
  width: number
  height: number
  /** True when the picker judged the photograph to be of the subject.
   *  False ones are still offered — the reviewer decides. */
  matched: boolean
  /** What this photograph would publish with, or null when nobody can be named. */
  credit: string | null
  /** The archive article it comes from; null for a manufacturer's photo. */
  article: { article_id: number; title: string | null; slug: string | null } | null
  /** A manufacturer's photo (from their own site, under the titles' agreements). */
  maker?: { brand: string | null; page_url: string | null; page_title: string | null } | null
  /** The vision check's score (0-3) and what it saw, when it looked at this photograph. */
  fit?: number | null
  fit_reason?: string | null
  /** Found in the title's archive rather than the piece's own sources. */
  from_archive?: boolean
  /** Other live articles that already lead with this photograph; these come last. */
  used_on?: { article_id: number; title: string | null; slug: string | null; site_type: string }[]
}
export type SectionPhotoOptions = {
  items: SectionPhotoOption[]
  /** The piece's sources hold no photographs: the items are the title's newest instead. */
  recent?: boolean
}
export type SectionQueueItem = {
  id: number
  title: string | null
  queued_at: string | null
  queue_position: number | null
  word_count: number
  featured_caption_source: string | null
}
export type SectionQueueCadence = {
  /** 0 = Monday. As stored, wall-clock in `tz`. */
  days_of_week: number[]
  release_time: string
  /** Zone the stored slots are read in; pre-E4 rows are UTC. */
  tz: string
  /** The same slots as Eastern wall-clock (today's offset). */
  days_of_week_et: number[]
  release_time_et: string
}
export type SectionQueueView = {
  items: SectionQueueItem[]
  cadence: SectionQueueCadence | null
  /** null when no cadence is saved — nothing is scheduled to go out. */
  next_release_at: string | null
  /** The release job's other gate (settings.section_queue_enabled). A
   *  cadence can be saved while this is false — the job requires both. */
  enabled: boolean
}
export type SectionTopic = {
  label: string; search_intent: string; representative_query: string; queries: string[]; impressions: number; clicks: number; position: number; score: number
  gsc_score: number; interest_bonus: number; coverage: number
  signals: { provider: string; label: string; strength: number; evidence: string; url: string | null; source_date: string | null; observed_at: string | null }[]
  draft: { id: number; status: SectionDraftStatus; title: string | null; created_at: string | null } | null
  piece_type?: string | null
}
export type DraftStatus = {
  running: boolean; stage?: string | null; stage_at?: string | null; started_at?: string | null; topic_label?: string | null
  requested_by?: string | null
  /** The piece a revise or recheck is working on, and which of the two it is. */
  draft_id?: number | null; kind?: 'revise' | 'recheck' | null
  /** The run (one per title at a time), and the pieces it has already saved. */
  run_id?: number; result_draft_ids?: number[]
  last?: { ok: boolean; draft_id: number | null; topic_label: string | null; error: string | null; finished_at: string; started_at: string | null } | null
}
export type SectionSignal = {
  provider: string; label: string; terms: string[]; strength: number; evidence: string; url: string | null; observed_at: string | null; source_date: string | null
}

// ---------- Check intake (/api/offline) ----------

/** A possible duplicate of a form that has not been saved yet. `score` is
 *  the pair score and `tier` the dedup classification ('auto', 'review', …);
 *  both come straight from the matcher, so neither is a promise. */
export type OfflineCandidate = {
  subscriber_id: number
  name: string
  /** `member_account_number`, falling back to the SimpleCirc id. */
  account_number: string | null
  zipcode: string | null
  score: number
  tier: string
}

/**
 * What `/api/offline/member` and `/api/offline/payment` return.
 *
 * `subscriber_id` / `member_account_number` are present only on `/member`,
 * which mints them — `/payment` was handed an existing member and does not
 * echo one back. `amount_paid` / `amount_due` are serialised Decimals, so
 * they arrive as strings.
 */
export type OfflineIntakeResult = {
  subscription_id: number
  order_key: string
  current_period_end: string | null
  amount_paid: string | null
  amount_due: string | null
  subscriber_id?: number
  member_account_number?: string | null
  gift_recipient_subscriber_id?: number | null
}

/** `/api/offline/subscriber` — a postal record with no payment behind it. */
export type OfflineSubscriberResult = {
  subscriber_id: number
  member_account_number: string | null
}

/** `/api/offline/void` — a check that never cleared. */
export type OfflineVoidResult = {
  subscription_id: number
  current_period_end: string | null
  status: string
  months_reversed: number | null
}

/** One row of the price list (`/api/admin/settings/plans`). The term the
 *  membership is granted comes from here, never from the amount paid. */
export type MembershipPlan = {
  publication_slug: string
  plan_id: string
  amount_cents: number
  currency: string
  term_months: number
  active: boolean
}
